export const CLUB_ZONE_FALLBACK = 'Europe/Moscow';

interface ClubParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const unparsable = (iso: string): boolean => Number.isNaN(new Date(iso).getTime());

const partsOf = (iso: string, zone: string): ClubParts | null => {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  try {
    const got = new Intl.DateTimeFormat('en-CA', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(at);
    const pick = (type: Intl.DateTimeFormatPartTypes): number =>
      Number(got.find((p) => p.type === type)?.value ?? Number.NaN);
    const out = {
      year: pick('year'),
      month: pick('month'),
      day: pick('day'),
      hour: pick('hour') % 24,
      minute: pick('minute'),
      second: pick('second'),
    };
    return Object.values(out).some(Number.isNaN) ? null : out;
  } catch {
    return null;
  }
};

const pad2 = (n: number): string => String(n).padStart(2, '0');

export const clubHourOf = (iso: string, zone: string): number =>
  partsOf(iso, zone)?.hour ?? new Date(iso).getHours();

export const formatClubTime = (iso: string, zone: string): string => {
  if (unparsable(iso)) return iso;
  const p = partsOf(iso, zone);
  if (!p) {
    const d = new Date(iso);
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  }
  return `${pad2(p.hour)}:${pad2(p.minute)}`;
};

export const formatClubTimeWithSeconds = (iso: string, zone: string): string => {
  if (unparsable(iso)) return iso;
  const p = partsOf(iso, zone);
  if (!p) {
    const d = new Date(iso);
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
  }
  return `${pad2(p.hour)}:${pad2(p.minute)}:${pad2(p.second)}`;
};

export const clubDateOf = (iso: string, zone: string): string => {
  if (unparsable(iso)) return iso;
  const p = partsOf(iso, zone);
  if (!p) {
    const d = new Date(iso);
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  }
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
};

export const formatClubDateTime = (
  iso: string,
  zone: string,
  options: { year?: boolean; seconds?: boolean } = {},
): string => {
  if (unparsable(iso)) return iso;
  const { year = true, seconds = true } = options;
  const d = new Date(iso);
  const p = partsOf(iso, zone) ?? {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
    hour: d.getHours(),
    minute: d.getMinutes(),
    second: d.getSeconds(),
  };
  const date = `${pad2(p.day)}.${pad2(p.month)}${year ? `.${p.year}` : ''}`;
  const time = `${pad2(p.hour)}:${pad2(p.minute)}${seconds ? `:${pad2(p.second)}` : ''}`;
  return `${date}, ${time}`;
};

export const formatInClubZone = (
  iso: string,
  zone: string,
  options: Intl.DateTimeFormatOptions,
): string => {
  if (unparsable(iso)) return iso;
  try {
    return new Intl.DateTimeFormat('ru-RU', { ...options, timeZone: zone }).format(new Date(iso));
  } catch {
    return new Intl.DateTimeFormat('ru-RU', options).format(new Date(iso));
  }
};

export type ClubDatePreset = 'short' | 'medium' | 'long' | 'time' | 'datetime' | 'weekday' | 'dayMonth';

const CLUB_DATE_PRESETS: Record<ClubDatePreset, Intl.DateTimeFormatOptions> = {
  short: { day: '2-digit', month: '2-digit', year: 'numeric' },
  medium: { day: '2-digit', month: 'long', year: 'numeric' },
  long: { day: '2-digit', month: 'long', year: 'numeric', weekday: 'long' },
  time: { hour: '2-digit', minute: '2-digit', hour12: false },
  weekday: { weekday: 'short' },
  dayMonth: { day: '2-digit', month: 'short' },
  datetime: { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false },
};

export const formatClubDate = (iso: string, zone: string, preset: ClubDatePreset = 'short'): string =>
  formatInClubZone(iso, zone, CLUB_DATE_PRESETS[preset]);

